import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { PortalApiError, portalFetch } from "../api/client";
import { sendPortalConversation } from "../api/conversation";
import type { DiscoverySyncState } from "../api/discoveryLive";
import type {
	PortalConversationDto,
	PortalDiscoverySnapshotDto,
	PortalSpecReadinessDto,
	PortalTurnDto,
} from "../model";
import { MessageBody } from "./MessageBody";
import { MessageComposer } from "./MessageComposer";
import { PrototypeViewer } from "./PrototypeViewer";
import { RichText } from "./RichText";
import { needsFundingReasonLabel } from "./statusLabels";
import { VoiceComposer } from "./VoiceComposer";

type OutgoingMessage = {
	content: string;
	failed: boolean;
	clientRequestId: string;
};

type ChatPrototype = {
	artifactId: string;
	name: string;
	ticketTitle: string;
};

const ROUND_TIMEOUT_MS = 120_000;
const ROUND_WARNING_MS = 30_000;
const TYPING_HEARTBEAT_MS = 5_000;

const roundParticipantStatusLabel = {
	drafting: "Drafting privately",
	sent: "Contribution sent",
	cancelled: "Contribution cancelled",
	timedOut: "Timed out — draft not sent",
} as const;

export function portalTurnAttribution(turn: PortalTurnDto) {
	if (turn.actorType !== "portalUser") return "Project agent";
	if (turn.isCurrentActor) return turn.actorDisplayName ? `You · ${turn.actorDisplayName}` : "You";
	return turn.actorDisplayName ?? "Collaborator";
}

export function ConversationView({
	projectId,
	mapId,
	conversationId,
	voiceAvailable,
	agentModel,
	readiness,
	discoverySnapshot,
	syncState,
	onSpecUpdated,
	draftRequest,
	initialMessage,
	onInitialMessageSent,
	prototypes = [],
}: {
	projectId: string;
	mapId?: string;
	conversationId?: string;
	voiceAvailable?: boolean;
	agentModel: string;
	readiness: PortalSpecReadinessDto;
	discoverySnapshot?: PortalDiscoverySnapshotDto;
	syncState: DiscoverySyncState;
	onSpecUpdated?: () => Promise<void>;
	draftRequest?: { key: string; message: string };
	initialMessage?: { key: string; message: string };
	onInitialMessageSent?: () => void;
	prototypes?: ChatPrototype[];
}) {
	const hasDiscoverySnapshot = Boolean(discoverySnapshot && discoverySnapshot.map.id === mapId);
	const [conversation, setConversation] = useState(conversationId);
	const [turns, setTurns] = useState<PortalTurnDto[]>([]);
	const [stream, setStream] = useState("");
	const [status, setStatus] = useState("");
	const [sending, setSending] = useState(false);
	const [standaloneAgentWorking, setStandaloneAgentWorking] = useState(false);
	// A shared snapshot owns run status; older fetches and local request cleanup cannot overwrite it.
	const agentWorking = hasDiscoverySnapshot
		? (discoverySnapshot!.conversation?.agentResponding ?? false)
		: standaloneAgentWorking;
	const [outgoingMessage, setOutgoingMessage] = useState<OutgoingMessage | null>(null);
	const [roundJoined, setRoundJoined] = useState(false);
	const [roundSecondsRemaining, setRoundSecondsRemaining] = useState<number | null>(null);
	const [roundNotice, setRoundNotice] = useState("");
	const composerRef = useRef<HTMLElement>(null);
	const composerTopRef = useRef<number | null>(null);
	const latestRef = useRef<HTMLDivElement>(null);
	const requestRef = useRef<{ content: string; key: string } | null>(null);
	const initialMessageSentRef = useRef<string | undefined>(undefined);
	const conversationRef = useRef(conversationId);
	const conversationCreationRef = useRef<Promise<string> | null>(null);
	const typingAdmissionRef = useRef<Promise<void> | null>(null);
	const typingTimerRef = useRef<number | null>(null);
	const lastTypingRequestAtRef = useRef(0);
	const lastTypedAtRef = useRef<number | null>(null);
	const timeoutStartedRef = useRef(false);
	const preserveVisibleComposer = useCallback(() => {
		const composer = composerRef.current;
		if (!composer) return;
		const bounds = composer.getBoundingClientRect();
		composerTopRef.current = bounds.bottom > 0 && bounds.top < window.innerHeight ? bounds.top : null;
	}, []);
	useLayoutEffect(() => {
		const previousTop = composerTopRef.current;
		composerTopRef.current = null;
		if (previousTop === null) return;
		const currentTop = composerRef.current?.getBoundingClientRect().top;
		if (currentTop === undefined) return;
		const offset = currentTop - previousTop;
		if (Math.abs(offset) > 1) window.scrollBy({ top: offset, behavior: "auto" });
	});
	// biome-ignore lint/correctness/useExhaustiveDependencies: Refreshed turns change the thread height after the run-state render.
	useLayoutEffect(() => {
		if (!outgoingMessage && !stream && !agentWorking) return;
		const latest = latestRef.current;
		if (latest) latest.scrollTop = latest.scrollHeight;
	}, [agentWorking, outgoingMessage, stream, turns]);
	useEffect(() => {
		setConversation(conversationId);
		conversationRef.current = conversationId;
		setTurns([]);
		setStream("");
		setStandaloneAgentWorking(false);
		setOutgoingMessage(null);
		setRoundJoined(false);
		setRoundSecondsRemaining(null);
		setRoundNotice("");
		lastTypedAtRef.current = null;
		timeoutStartedRef.current = false;
		requestRef.current = null;
		if (conversationId) {
			return;
		}
		if (!mapId) return;
		portalFetch<{ items: PortalConversationDto[] }>(
			`/api/portal/v1/projects/${projectId}/conversations?limit=50&offset=0&mapId=${encodeURIComponent(mapId)}`,
		)
			.then((page) => {
				const currentConversation = page.items[0]?.id;
				conversationRef.current = currentConversation;
				setConversation(currentConversation);
				setStandaloneAgentWorking(page.items[0]?.agentResponding ?? false);
			})
			.catch(() => setStatus("The conversation could not be loaded."));
	}, [conversationId, mapId, projectId]);
	useEffect(() => {
		if (!discoverySnapshot || discoverySnapshot.map.id !== mapId) return;
		preserveVisibleComposer();
		conversationRef.current = discoverySnapshot.conversation?.id;
		setConversation(discoverySnapshot.conversation?.id);
		setTurns(discoverySnapshot.turns);
		setStatus("");
	}, [discoverySnapshot, mapId, preserveVisibleComposer]);
	async function ensureConversation() {
		if (conversationRef.current) return conversationRef.current;
		if (!mapId) throw new Error("A Wayfinder map must be selected before starting a conversation.");
		if (!conversationCreationRef.current) {
			conversationCreationRef.current = portalFetch<PortalConversationDto>(
				`/api/portal/v1/projects/${projectId}/conversations`,
				{
					method: "POST",
					body: JSON.stringify({ title: "Wayfinder conversation", workMapId: mapId }),
				},
			).then((created) => {
				conversationRef.current = created.id;
				setConversation(created.id);
				setStandaloneAgentWorking(created.agentResponding);
				return created.id;
			});
		}
		try {
			return await conversationCreationRef.current;
		} finally {
			conversationCreationRef.current = null;
		}
	}
	async function postTyping() {
		const id = await ensureConversation();
		await portalFetch(`/api/portal/v1/projects/${projectId}/conversations/${id}/response-round/typing`, {
			method: "POST",
			body: JSON.stringify({}),
		});
		setRoundJoined(true);
		setRoundNotice("");
		lastTypingRequestAtRef.current = Date.now();
	}
	function notifyTyping() {
		const typedAt = Date.now();
		lastTypedAtRef.current = typedAt;
		setRoundJoined(true);
		setRoundSecondsRemaining(Math.ceil(ROUND_TIMEOUT_MS / 1_000));
		setRoundNotice("");
		timeoutStartedRef.current = false;
		const elapsed = typedAt - lastTypingRequestAtRef.current;
		if (!typingAdmissionRef.current && elapsed >= TYPING_HEARTBEAT_MS) {
			typingAdmissionRef.current = postTyping()
				.catch(() => {
					setRoundJoined(false);
					setStatus("Your contribution could not join the shared response round.");
				})
				.finally(() => {
					typingAdmissionRef.current = null;
				});
			return;
		}
		if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
		typingTimerRef.current = window.setTimeout(
			() => {
				typingTimerRef.current = null;
				if (typingAdmissionRef.current) return;
				typingAdmissionRef.current = postTyping()
					.catch(() => setStatus("Your typing activity could not be synchronized."))
					.finally(() => {
						typingAdmissionRef.current = null;
					});
			},
			Math.max(0, TYPING_HEARTBEAT_MS - elapsed),
		);
	}
	const load = useCallback(
		async (conversationToLoad?: string, preserveComposer = false) => {
			const id = conversationToLoad ?? conversation;
			if (!id) return;
			const page = await portalFetch<{ items: PortalTurnDto[] }>(
				`/api/portal/v1/projects/${projectId}/conversations/${id}/turns?limit=50&offset=0&latest=true`,
			);
			if (preserveComposer) preserveVisibleComposer();
			setTurns(page.items);
		},
		[conversation, preserveVisibleComposer, projectId],
	);
	useEffect(() => {
		load();
	}, [load]);
	async function releaseRound(operation: "cancel" | "timeout") {
		setSending(true);
		setStream("");
		if (typingTimerRef.current !== null) {
			window.clearTimeout(typingTimerRef.current);
			typingTimerRef.current = null;
		}
		try {
			await typingAdmissionRef.current;
			const id = await ensureConversation();
			const result = await sendPortalConversation({
				url: `/api/portal/v1/projects/${projectId}/conversations/${id}/response-round/${operation}`,
				onTextDelta: (text) => {
					preserveVisibleComposer();
					setStandaloneAgentWorking(true);
					setStream((current) => current + text);
				},
				onReconnect: () => setStatus("Connection interrupted. Reconnecting automatically…"),
			});
			if (
				operation === "timeout" &&
				result.roundRelease?.accepted !== true &&
				result.roundRelease?.retryAfterMs !== undefined
			) {
				const retryAfterMs = Math.max(1, result.roundRelease.retryAfterMs);
				lastTypedAtRef.current = Date.now() - (ROUND_TIMEOUT_MS - retryAfterMs);
				setRoundSecondsRemaining(Math.ceil(retryAfterMs / 1_000));
				timeoutStartedRef.current = false;
				setStatus("The server reports recent typing activity. Timeout will retry automatically.");
				return;
			}
			setRoundJoined(false);
			setRoundSecondsRemaining(null);
			lastTypedAtRef.current = null;
			setRoundNotice(
				operation === "timeout"
					? "Your response round timed out. Your private draft is still here and was not submitted."
					: "Contribution cancelled. Your private draft is still here and was not submitted.",
			);
			setStandaloneAgentWorking(false);
			await load(id, true);
			await onSpecUpdated?.();
			setStream("");
		} catch {
			if (operation === "timeout") timeoutStartedRef.current = false;
			setStatus(
				operation === "timeout"
					? "The inactivity timeout could not be confirmed. Retrying automatically…"
					: "Your contribution could not be cancelled.",
			);
		} finally {
			setSending(false);
		}
	}
	function clearDraftRoundState() {
		setRoundJoined(false);
		setRoundSecondsRemaining(null);
		lastTypedAtRef.current = null;
		timeoutStartedRef.current = true;
	}
	useEffect(() => {
		if (!roundJoined || lastTypedAtRef.current === null) return;
		const updateDeadline = () => {
			const remaining = ROUND_TIMEOUT_MS - (Date.now() - lastTypedAtRef.current!);
			setRoundSecondsRemaining(Math.max(0, Math.ceil(remaining / 1_000)));
			if (remaining <= 0 && !timeoutStartedRef.current) {
				timeoutStartedRef.current = true;
				void releaseRound("timeout");
			}
		};
		updateDeadline();
		const interval = window.setInterval(updateDeadline, 1_000);
		return () => window.clearInterval(interval);
	});
	useEffect(
		() => () => {
			if (typingTimerRef.current !== null) window.clearTimeout(typingTimerRef.current);
		},
		[],
	);
	useEffect(() => {
		if (!conversation || !agentWorking || sending) return;
		const poll = window.setInterval(async () => {
			try {
				// Discovery owns an atomic snapshot of both run status and saved turns.
				if (hasDiscoverySnapshot && onSpecUpdated) {
					await onSpecUpdated();
					return;
				}
				const page = await portalFetch<{ items: PortalConversationDto[] }>(
					`/api/portal/v1/projects/${projectId}/conversations?limit=50&offset=0&mapId=${encodeURIComponent(mapId ?? "")}`,
				);
				const stillWorking = page.items.find((item) => item.id === conversation)?.agentResponding ?? false;
				await load(conversation, true);
				setStandaloneAgentWorking(stillWorking);
				setStatus("");
				if (!stillWorking) await onSpecUpdated?.();
			} catch {
				setStatus("Reconnecting to the project agent automatically…");
			}
		}, 2_000);
		return () => window.clearInterval(poll);
	}, [agentWorking, conversation, hasDiscoverySnapshot, load, mapId, onSpecUpdated, projectId, sending]);
	async function respond(message: string): Promise<boolean> {
		if (sending || !message.trim()) return false;
		if (typingTimerRef.current !== null) {
			window.clearTimeout(typingTimerRef.current);
			typingTimerRef.current = null;
		}
		// The private draft has been submitted. Stop its inactivity deadline before
		// waiting for admission or the agent stream to finish.
		clearDraftRoundState();
		await typingAdmissionRef.current;
		const request =
			requestRef.current?.content === message ? requestRef.current : { content: message, key: crypto.randomUUID() };
		requestRef.current = request;
		setOutgoingMessage({ content: message, failed: false, clientRequestId: request.key });
		setSending(true);
		setStandaloneAgentWorking(true);
		setStatus("");
		setStream("");
		try {
			const id = await ensureConversation();
			await sendPortalConversation({
				url: `/api/portal/v1/projects/${projectId}/conversations/${id}/respond`,
				content: message,
				idempotencyKey: request.key,
				onTextDelta: (text) => {
					preserveVisibleComposer();
					setStatus("");
					setStream((current) => current + text);
				},
				onReconnect: (reason) => {
					setStream("");
					setStatus(
						reason === "busy"
							? "The project agent is finishing the previous response. Your message will send automatically."
							: "Connection interrupted. Reconnecting automatically…",
					);
				},
			});
			requestRef.current = null;
			setStatus("");
			clearDraftRoundState();
			setStandaloneAgentWorking(false);
			await load(id, true);
			setOutgoingMessage(null);
			await onSpecUpdated?.();
			setStream("");
			return true;
		} catch (error) {
			if (error instanceof PortalApiError && error.code === "conversationBusy") {
				setStandaloneAgentWorking(true);
				setStatus("The project agent is still working. This page will reconnect automatically.");
			} else if (error instanceof PortalApiError && error.code === "needsFunding" && error.reason) {
				setStandaloneAgentWorking(false);
				setOutgoingMessage({ content: message, failed: true, clientRequestId: request.key });
				setStatus(`Needs funding. ${needsFundingReasonLabel(error.reason)}`);
			} else if (error instanceof PortalApiError) {
				setStandaloneAgentWorking(false);
				setOutgoingMessage({ content: message, failed: true, clientRequestId: request.key });
				setStatus(`${error.message}. You can try again.`);
			} else {
				setStandaloneAgentWorking(false);
				setOutgoingMessage({ content: message, failed: true, clientRequestId: request.key });
				setStatus("The project agent could not respond. You can try again.");
			}
			return false;
		} finally {
			setSending(false);
		}
	}
	useEffect(() => {
		if (
			!initialMessage ||
			initialMessageSentRef.current === initialMessage.key ||
			!hasDiscoverySnapshot ||
			sending ||
			agentWorking
		)
			return;
		initialMessageSentRef.current = initialMessage.key;
		onInitialMessageSent?.();
		void respond(initialMessage.message);
	});
	function continueDiscovery() {
		return respond(
			"Continue the spec discovery. Review the current requirements and assessment, then ask me the single highest-priority unanswered question. Include your recommended answer.",
		);
	}
	function annotatePrototype(prototype: ChatPrototype, requestedChange: string) {
		const fileName = prototype.name.split("/").at(-1) ?? prototype.name;
		void respond(
			`Prototype annotation for “${fileName}” (${prototype.artifactId}), linked to “${prototype.ticketTitle}”:\n\n${requestedChange}`,
		);
		return Promise.resolve(true);
	}
	async function deleteVoice(turn: PortalTurnDto) {
		if (
			!turn.voiceRecordId ||
			!window.confirm("Delete this voice recording? The transcript remains in the conversation.")
		)
			return;
		try {
			await portalFetch(`/api/portal/v1/projects/${projectId}/voice/${turn.voiceRecordId}`, {
				method: "DELETE",
				body: JSON.stringify({ reason: "Deleted by the collaborator" }),
			});
			setStatus("Voice recording deleted. The transcript remains.");
			await load(undefined, true);
		} catch {
			setStatus("Voice recording could not be deleted.");
		}
	}
	const latestAgentTurnId = [...turns]
		.reverse()
		.find((turn) => turn.actorType === "agent" && turn.status === "complete")?.id;
	useEffect(() => {
		if (!latestAgentTurnId) return;
		const latest = latestRef.current;
		if (latest) latest.scrollTo({ top: latest.scrollHeight, behavior: "smooth" });
	}, [latestAgentTurnId]);
	const recentTurns = turns.slice(-12);
	// Live snapshots can include the saved turn before the response request finishes.
	const outgoingMessageCommitted = turns.some(
		(turn) => turn.isCurrentActor && turn.clientRequestId === outgoingMessage?.clientRequestId,
	);
	const sharedResponseRound =
		discoverySnapshot && discoverySnapshot.map.id === mapId ? discoverySnapshot.responseRound : null;
	const sharedAgentRun = discoverySnapshot && discoverySnapshot.map.id === mapId ? discoverySnapshot.agentRun : null;
	const workingInitiator = sharedAgentRun?.status === "working" ? sharedAgentRun.initiator : null;
	const terminalRunNotice =
		sharedAgentRun?.status === "failed"
			? `The response started by ${sharedAgentRun.initiator.isCurrentActor ? "you" : sharedAgentRun.initiator.displayName} failed. You can try again.`
			: sharedAgentRun?.status === "cancelled"
				? `The response started by ${sharedAgentRun.initiator.isCurrentActor ? "you" : sharedAgentRun.initiator.displayName} was cancelled.`
				: "";
	return (
		<aside
			ref={composerRef}
			className="conversation-record conversation-composer agent-chat-dock"
			aria-labelledby="project-agent-heading"
		>
			<header className="agent-chat-dock-heading">
				<div>
					<h3 id="project-agent-heading">Project Agent</h3>
					<small>{agentModel}</small>
				</div>
				<div className="agent-chat-dock-actions">
					<div
						className={`discovery-sync-status discovery-sync-status--${syncState}`}
						role="status"
						aria-live="polite"
					>
						<span aria-hidden="true" />
						<strong>
							{syncState === "connected"
								? "Live updates connected"
								: syncState === "reconnecting"
									? "Reconnecting"
									: "Updates temporarily paused"}
						</strong>
						{syncState !== "connected" && (
							<small>
								{syncState === "degraded" ? "Reconnecting automatically" : "Reading remains available"}
							</small>
						)}
					</div>
					{readiness.stage === "discovery" && (
						<button
							type="button"
							className="secondary"
							onClick={continueDiscovery}
							disabled={sending || agentWorking}
						>
							Ask next question
						</button>
					)}
				</div>
			</header>
			<div ref={latestRef} className="agent-chat-latest" aria-live="polite">
				{sharedResponseRound && sharedResponseRound.participants.length > 0 && (
					<section className="response-round-status" aria-labelledby="response-round-heading">
						<header>
							<div>
								<h4 id="response-round-heading">Shared response round</h4>
								<p>One agent response starts after every joined collaborator is ready.</p>
							</div>
							<small>Draft text stays private</small>
						</header>
						<ul aria-label="Response round participants">
							{sharedResponseRound.participants.map((participant) => (
								<li key={participant.portalUserId} data-status={participant.status}>
									<strong>{participant.displayName}</strong>
									<span>{roundParticipantStatusLabel[participant.status]}</span>
								</li>
							))}
						</ul>
					</section>
				)}
				{prototypes.length > 0 && (
					<section className="agent-chat-prototypes" aria-labelledby="prototype-review-heading">
						<header>
							<div>
								<span>Prototype review</span>
								<h4 id="prototype-review-heading">Try the current visual decisions</h4>
							</div>
							<small>Open and annotate without leaving this conversation.</small>
						</header>
						<ul>
							{prototypes.map((prototype) => (
								<li key={prototype.artifactId}>
									<div>
										<strong>{prototype.name.split("/").at(-1)}</strong>
										<small>
											Linked decision: <RichText content={prototype.ticketTitle} inline />
										</small>
									</div>
									<PrototypeViewer
										projectId={projectId}
										artifact={prototype}
										annotationDisabled={sending || agentWorking}
										onAnnotate={(requestedChange) => annotatePrototype(prototype, requestedChange)}
									/>
								</li>
							))}
						</ul>
					</section>
				)}
				{recentTurns.length === 0 && !outgoingMessage && !agentWorking && !stream ? (
					<p>Ask in your own words. The agent will keep the Wayfinder map and living Spec in step.</p>
				) : null}
				{(recentTurns.length > 0 || outgoingMessage || stream) && (
					<ol className="agent-chat-thread">
						{recentTurns.map((turn) => (
							<li key={turn.id} className={turn.actorType === "portalUser" ? "from-user" : "from-agent"}>
								<span>{portalTurnAttribution(turn)}</span>
								<MessageBody content={turn.content} markdown={turn.actorType !== "portalUser"} />
								{turn.voiceRecordId && (
									<button type="button" className="text-button" onClick={() => deleteVoice(turn)}>
										Delete voice recording
									</button>
								)}
							</li>
						))}
						{outgoingMessage && !outgoingMessageCommitted && (
							<li className={`from-user outgoing-message${outgoingMessage.failed ? " failed" : ""}`}>
								<span>You</span>
								<MessageBody content={outgoingMessage.content} markdown={false} />
								{outgoingMessage.failed && (
									<div className="message-send-failure">
										<small>Message failed to send.</small>
										<button type="button" onClick={() => void respond(outgoingMessage.content)}>
											Resend message
										</button>
									</div>
								)}
							</li>
						)}
						{stream && (
							<li className="from-agent streaming">
								<span>Project agent</span>
								<MessageBody content={stream} markdown />
							</li>
						)}
					</ol>
				)}
				{/* Keep local work visible through snapshot refreshes and the final saved-turn fetch. */}
				{(sending || agentWorking) && !stream && (
					<div className="agent-thinking">
						<span className="agent-thinking-dots" aria-hidden="true">
							<i />
							<i />
							<i />
						</span>
						<span>
							<strong>Project agent is thinking</strong>
							<small>
								{workingInitiator
									? `${workingInitiator.isCurrentActor ? "You" : workingInitiator.displayName} started this response. `
									: ""}
								The response will keep running if you leave this page.
							</small>
						</span>
					</div>
				)}
			</div>
			<MessageComposer
				id="message"
				label="Ask the project agent"
				busy={sending || agentWorking}
				onSend={respond}
				onTyping={notifyTyping}
				onCancel={roundJoined ? () => releaseRound("cancel") : undefined}
				clearOnSubmit
				draftRequest={draftRequest}
			>
				{conversation && (
					<VoiceComposer
						targetType="projects"
						targetId={projectId}
						conversationId={conversation}
						onTurnCommitted={() => {
							preserveVisibleComposer();
							setStandaloneAgentWorking(true);
							return hasDiscoverySnapshot && onSpecUpdated ? onSpecUpdated() : load(undefined, true);
						}}
						available={voiceAvailable}
						disabled={sending || agentWorking}
					/>
				)}
			</MessageComposer>
			<div role="status" aria-live="polite">
				{roundJoined && roundSecondsRemaining !== null && roundSecondsRemaining <= ROUND_WARNING_MS / 1_000
					? `Your contribution will time out in ${roundSecondsRemaining} seconds unless you keep typing or send it. Your draft will remain private and stay in this field.`
					: roundNotice || status || terminalRunNotice}
			</div>
		</aside>
	);
}
